// Order confirmation. Reached straight after a successful POST /api/orders,
// and also where Stripe redirects after Checkout (?paid=1 / ?cancelled=1).
// If the order is still pending_payment, shows a "Pay now" button that
// creates a Checkout Session and redirects.
import { useEffect, useState } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import TopBar from '../components/TopBar'
import { apiFetch } from '../lib/api'

interface OrderItem { id: string; size?: string | null; qty: number; lengthCm?: number | null; unitPriceCents: number; lineTotalCents: number }
interface Order {
  id: string; number: number; status: string; totalCents: number; subtotalCents: number; shippingCents: number; currency: string
  guestEmail: string | null
  shippingSnapshot: { fullName: string; email: string; line1: string; line2?: string; city: string; region?: string; postcode: string; country: string }
  createdAt: string
}

const STATUS_TONE: Record<string, string> = {
  pending_payment: 'bg-amber-100 text-amber-800',
  paid:            'bg-emerald-100 text-emerald-800',
  in_production:   'bg-blue-100 text-blue-800',
  shipped:         'bg-indigo-100 text-indigo-800',
  delivered:       'bg-teal-100 text-teal-800',
  cancelled:       'bg-red-100 text-red-700',
  refunded:        'bg-rose-100 text-rose-800',
}

export default function OrderConfirm() {
  const { id } = useParams<{ id: string }>()
  const { getAccessToken, user } = useAuth()
  const [params] = useSearchParams()
  const justPaid    = params.get('paid') === '1'
  const justCancelled = params.get('cancelled') === '1'
  const [data, setData] = useState<{ order: Order; items: OrderItem[] } | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [payBusy, setPayBusy] = useState(false)

  // Re-poll once on mount + on ?paid=1 (Stripe redirects faster than webhooks,
  // so the first refetch may still show 'pending_payment'; we retry briefly).
  useEffect(() => {
    let mounted = true
    let attempts = 0
    async function load() {
      try {
        const d = await apiFetch<{ order: Order; items: OrderItem[] }>(
          getAccessToken as unknown as () => Promise<string | undefined>,
          `/api/orders/${id}`,
        )
        if (!mounted) return
        setData(d); setErr(null)
        // If we just came back from a successful Checkout but the order still
        // shows pending (webhook race), retry a few times with backoff.
        if (justPaid && d.order.status === 'pending_payment' && attempts < 5) {
          attempts++; setTimeout(load, 1200)
        }
      } catch (e) { if (mounted) setErr((e as Error).message) }
    }
    void load()
    return () => { mounted = false }
  }, [id, getAccessToken, justPaid])

  async function payNow() {
    if (!data) return
    setPayBusy(true)
    try {
      const r = await apiFetch<{ url: string }>(
        getAccessToken as unknown as () => Promise<string | undefined>,
        `/api/payments/checkout/${data.order.id}`,
        { method: 'POST' },
      )
      if (r?.url) window.location.assign(r.url)
      else throw new Error('No checkout URL returned')
    } catch (e) {
      const msg = (e as Error).message
      if (msg.includes('not configured')) alert('Payments are not enabled yet. EVRST Studio will follow up with an invoice.')
      else alert(`Could not start checkout: ${msg}`)
      setPayBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <TopBar title={justPaid ? 'Payment received' : 'Order received'} />
      <main className="flex-1 p-8">
        <div className="max-w-2xl mx-auto">
          {err && <p className="text-red-600">{err}</p>}
          {!data && !err && <p className="text-ink-3 text-sm">Loading…</p>}
          {data && (
            <>
              {justPaid && data.order.status !== 'pending_payment' && (
                <div className="card bg-emerald-50 border-emerald-200 text-emerald-900 text-center mb-6">
                  <div className="eyebrow !text-emerald-700">Payment received</div>
                  <h2 className="font-heading uppercase font-bold text-2xl">Thanks — your payment cleared.</h2>
                  <p className="text-sm mt-1">We'll get this into production and email you when it ships.</p>
                </div>
              )}
              {justCancelled && (
                <div className="card bg-amber-50 border-amber-200 text-amber-900 text-center mb-6">
                  <div className="eyebrow !text-amber-700">Checkout cancelled</div>
                  <p className="text-sm mt-1">No payment was taken. You can pay below whenever you're ready.</p>
                </div>
              )}

              <div className="card text-center">
                <div className="eyebrow">Order received</div>
                <h1 className="font-heading uppercase font-bold text-3xl text-ink">Thanks — we're on it.</h1>
                <p className="text-ink-2 mt-3">Order <strong className="text-ink">#{data.order.number}</strong>.
                  <span className={'ml-2 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ' + (STATUS_TONE[data.order.status] ?? 'bg-gray-100 text-gray-700')}>{data.order.status.replace(/_/g, ' ')}</span>
                </p>
              </div>

              {data.order.status === 'pending_payment' && (
                <div className="card mt-6 text-center bg-gold/5 border-gold/30">
                  <div className="eyebrow">Next step</div>
                  <h3 className="font-heading uppercase font-bold text-xl text-ink">Pay securely with Stripe</h3>
                  <p className="text-ink-2 mt-2 text-sm">${(data.order.totalCents / 100).toFixed(2)} {data.order.currency}. We'll start production as soon as payment clears.</p>
                  <button onClick={payNow} disabled={payBusy} className="btn-gold mt-4">{payBusy ? 'Opening Stripe…' : 'Pay now'}</button>
                </div>
              )}

              <div className="card mt-6">
                <div className="eyebrow">Summary</div>
                <div className="space-y-2 text-sm">
                  {data.items.map((it) => (
                    <div key={it.id} className="flex justify-between border-b border-border pb-1.5">
                      <span className="text-ink-2">
                        {it.size && <span className="font-semibold text-ink uppercase mr-2">{it.size}</span>}
                        {it.lengthCm && <span className="font-semibold text-ink mr-2">{it.lengthCm} cm</span>}
                        × {it.qty}
                      </span>
                      <span className="text-ink font-semibold">${(it.lineTotalCents / 100).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-base pt-2"><span className="text-ink-3">Total ({data.order.currency})</span><span className="font-bold text-ink">${(data.order.totalCents / 100).toFixed(2)}</span></div>
                </div>
              </div>

              <div className="card mt-6">
                <div className="eyebrow">Shipping to</div>
                <div className="text-sm text-ink-2 leading-relaxed">
                  <div className="text-ink font-semibold">{data.order.shippingSnapshot.fullName}</div>
                  <div>{data.order.shippingSnapshot.line1}{data.order.shippingSnapshot.line2 ? `, ${data.order.shippingSnapshot.line2}` : ''}</div>
                  <div>{data.order.shippingSnapshot.city}{data.order.shippingSnapshot.region ? `, ${data.order.shippingSnapshot.region}` : ''} {data.order.shippingSnapshot.postcode}</div>
                  <div>{data.order.shippingSnapshot.country}</div>
                  <div className="text-ink-3 mt-2">{data.order.shippingSnapshot.email}</div>
                </div>
              </div>

              <div className="card mt-6">
                <div className="eyebrow">What's next</div>
                <ol className="list-decimal pl-5 text-sm text-ink-2 space-y-1">
                  {data.order.status === 'pending_payment' ? (
                    <>
                      <li>Use the <strong className="text-ink">Pay now</strong> button above to settle the invoice.</li>
                      <li>Once payment clears we approve the artwork and start production.</li>
                      <li>You'll get a shipping email with tracking when it leaves us.</li>
                    </>
                  ) : (
                    <>
                      <li>We're checking your artwork and getting it on the press.</li>
                      <li>You'll get a shipping email with tracking as soon as it leaves us.</li>
                      <li>Questions? <a href="mailto:hello@evrststudio.com" className="text-gold underline">hello@evrststudio.com</a>.</li>
                    </>
                  )}
                </ol>
              </div>

              <div className="mt-8 text-center">
                <Link to="/" className="btn-outline">Back to designer</Link>
              </div>
              {!user && <p className="text-[11px] text-ink-3 mt-3 text-center">Sign in next time to track your orders in one place.</p>}
            </>
          )}
        </div>
      </main>
    </div>
  )
}
